import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

const TIERS = [
  { id: "evaluation", title: "Manuscript Evaluation", price: "199", note: "Starting at", desc: "Comprehensive editorial assessment of structure, voice, marketability, and revision priorities.", features: ["20–30 page editorial letter", "Strengths + opportunity map", "Revision roadmap"] },
  { id: "formatting", title: "Professional Formatting", price: "299", note: "Starting at", desc: "Print- and digital-ready interior design tailored to your genre and length.", features: ["Print + EPUB output", "Custom typography", "Front + back matter"] },
  { id: "cover", title: "Cover Design", price: "499", note: "Starting at", desc: "Custom hardcover and paperback art direction built around your story.", features: ["3 design concepts", "Print + ebook variants", "Spine + back panel"] },
  { id: "package", title: "Publishing Package", price: "1,499", note: "Starting at", desc: "Full-service publishing &mdash; from final edit to distribution.", features: ["Copy edit + format + cover", "ISBN + barcode", "Print-on-demand setup", "Distribution to major retailers"], featured: true },
  { id: "premium", title: "Premium Partnership", price: "Custom", note: "By invitation", desc: "Hand-selected authors enter our flagship list with full editorial, design, marketing, and tour support.", features: ["Hands-on editorial", "PR + media kit", "Speaking + tour booking", "Long-term author development"] },
];

export default function Services() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", project_summary: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/services/inquiry", form);
      toast.success("Inquiry received. A consultant will reach out within 2–3 business days.");
      setForm({ name: "", email: "", phone: "", service: "", project_summary: "" });
    } catch (err) {
      toast.error("Could not send inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="services-page">
      <Seo
        title="Publishing Services for Authors | 1123 Publishing Company"
        description="Professional publishing services from 1123 Publishing: manuscript evaluation, formatting, cover design, full publishing packages, and premium author partnerships."
        path="/services"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Book Publishing Services",
          provider: { "@type": "Organization", name: "1123 Publishing Company", url: "https://1123publishing.com" },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Publishing Services",
            itemListElement: TIERS.map((t) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: t.title },
            })),
          },
        }}
      />
      <section className="bg-navy text-ivory py-20 md:py-28 relative overflow-hidden">
        <div className="parchment-overlay" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="overline text-gold mb-4">Publishing Services</div>
          <h1 className="font-serif text-5xl md:text-6xl">A boutique imprint at your service.</h1>
          <p className="mt-6 text-ivory/80 max-w-2xl mx-auto leading-relaxed">
            Five tiers of editorial, design, and distribution support &mdash; for authors, educators,
            entrepreneurs, and organizations who want their work to stand on the right shelf.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {TIERS.map((t) => (
              <div key={t.id} data-testid={`service-${t.id}`}
                className={`bg-parchment p-8 md:p-10 border ${t.featured ? "border-gold" : "border-navy/15"} relative`}>
                {t.featured && (
                  <div className="absolute -top-3 left-8 bg-gold text-navy text-[10px] tracking-[0.25em] uppercase px-3 py-1">Most Popular</div>
                )}
                <div className="overline">{t.note}</div>
                <div className="font-serif text-3xl text-navy mt-2">{t.title}</div>
                <div className="mt-4 font-serif text-4xl text-gold">{t.price.startsWith('$') || t.price === 'Custom' ? t.price : `$${t.price}`}</div>
                <p className="text-charcoal/80 mt-5 text-sm leading-relaxed">{t.desc}</p>
                <ul className="mt-6 space-y-2 text-sm text-navy/80">
                  {t.features.map((f) => (
                    <li key={f} className="flex gap-2"><span className="text-gold mt-1">&#10086;</span> {f}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-parchment border-t border-navy/10">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="overline">Online Consultation</div>
            <h2 className="font-serif text-4xl md:text-5xl text-navy mt-2">Request a Consultation</h2>
            <p className="mt-4 text-charcoal/80">Tell us about your project. A publishing consultant will be in touch.</p>
          </div>

          <form onSubmit={submit} className="space-y-5 bg-white border border-navy/15 p-8" data-testid="inquiry-form">
            <div className="grid md:grid-cols-2 gap-5">
              <input required placeholder="Your name" value={form.name} onChange={update("name")} data-testid="inquiry-name"
                className="bg-parchment border border-navy/15 px-4 py-3 focus:border-gold focus:outline-none" />
              <input required type="email" placeholder="Email" value={form.email} onChange={update("email")} data-testid="inquiry-email"
                className="bg-parchment border border-navy/15 px-4 py-3 focus:border-gold focus:outline-none" />
            </div>
            <input placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} data-testid="inquiry-phone"
              className="w-full bg-parchment border border-navy/15 px-4 py-3 focus:border-gold focus:outline-none" />
            <select required value={form.service} onChange={update("service")} data-testid="inquiry-service"
              className="w-full bg-parchment border border-navy/15 px-4 py-3 focus:border-gold focus:outline-none">
              <option value="">Select a service&hellip;</option>
              {TIERS.map((t) => <option key={t.id} value={t.title}>{t.title}</option>)}
            </select>
            <textarea required rows={6} placeholder="Tell us about your project" value={form.project_summary} onChange={update("project_summary")} data-testid="inquiry-summary"
              className="w-full bg-parchment border border-navy/15 px-4 py-3 focus:border-gold focus:outline-none" />
            <button type="submit" disabled={loading} data-testid="inquiry-submit"
              className="w-full bg-navy text-ivory py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-charcoal disabled:opacity-60">
              {loading ? "Sending..." : "Request Consultation"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
